// Chinese lunisolar calendar, enough to resolve the PRC's statutory holidays.
//
// Month lengths and leap months for 1900–2099 are the usual 20-bit encoding
// (each year one integer): bits 4–16 are the twelve regular months, high to
// low, 1 meaning 30 days; bits 0–3 are the leap month (0 = none); bit 16 is
// the leap month's own length. 31 January 1900 is lunar 1/1/1900, which is
// the epoch those bits are counted from.
//
// Qingming is not a lunar date. The statute says 农历清明当日 — the day of
// the Qingming solar term — which is 4 or 5 April. The 21st-century formula
// below matches the Hong Kong Observatory's published dates.

const EPOCH = Date.UTC(1900, 0, 31)
const DAY = 86400000
const BASE = 1900
const LAST = 2099

// 1900 through 2100.
const YEARS = [
    0x04bd8, 0x04ae0, 0x0a570, 0x054d5, 0x0d260, 0x0d950, 0x16554, 0x056a0, 0x09ad0, 0x055d2,
    0x04ae0, 0x0a5b6, 0x0a4d0, 0x0d250, 0x1d255, 0x0b540, 0x0d6a0, 0x0ada2, 0x095b0, 0x14977,
    0x04970, 0x0a4b0, 0x0b4b5, 0x06a50, 0x06d40, 0x1ab54, 0x02b60, 0x09570, 0x052f2, 0x04970,
    0x06566, 0x0d4a0, 0x0ea50, 0x06e95, 0x05ad0, 0x02b60, 0x186e3, 0x092e0, 0x1c8d7, 0x0c950,
    0x0d4a0, 0x1d8a6, 0x0b550, 0x056a0, 0x1a5b4, 0x025d0, 0x092d0, 0x0d2b2, 0x0a950, 0x0b557,
    0x06ca0, 0x0b550, 0x15355, 0x04da0, 0x0a5d0, 0x14573, 0x052d0, 0x0a9a8, 0x0e950, 0x06aa0,
    0x0aea6, 0x0ab50, 0x04b60, 0x0aae4, 0x0a570, 0x05260, 0x0f263, 0x0d950, 0x05b57, 0x056a0,
    0x096d0, 0x04dd5, 0x04ad0, 0x0a4d0, 0x0d4d4, 0x0d250, 0x0d558, 0x0b540, 0x0b5a0, 0x195a6,
    0x095b0, 0x049b0, 0x0a974, 0x0a4b0, 0x0b27a, 0x06a50, 0x06d40, 0x0af46, 0x0ab60, 0x09570,
    0x04af5, 0x04970, 0x064b0, 0x074a3, 0x0ea50, 0x06b58, 0x055c0, 0x0ab60, 0x096d5, 0x092e0,
    0x0c960, 0x0d954, 0x0d4a0, 0x0da50, 0x07552, 0x056a0, 0x0abb7, 0x025d0, 0x092d0, 0x0cab5,
    0x0a950, 0x0b4a0, 0x0baa4, 0x0ad50, 0x055d9, 0x04ba0, 0x0a5b0, 0x15176, 0x052b0, 0x0a930,
    0x07954, 0x06aa0, 0x0ad50, 0x05b52, 0x04b60, 0x0a6e6, 0x0a4e0, 0x0d260, 0x0ea65, 0x0d530,
    0x05aa0, 0x076a3, 0x096d0, 0x04bd7, 0x04ad0, 0x0a4d0, 0x1d0b6, 0x0d250, 0x0d520, 0x0dd45,
    0x0b5a0, 0x056d0, 0x055b2, 0x049b0, 0x0a577, 0x0a4b0, 0x0aa50, 0x1b255, 0x06d20, 0x0ada0,
    0x14b63, 0x09370, 0x049f8, 0x04970, 0x064b0, 0x168a6, 0x0ea50, 0x06b20, 0x1a6c4, 0x0aae0,
    0x0a2e0, 0x0d2e3, 0x0c960, 0x0d557, 0x0d4a0, 0x0da50, 0x05d55, 0x056a0, 0x0a6d0, 0x055d4,
    0x052d0, 0x0a9b8, 0x0a950, 0x0b4a0, 0x0b6a6, 0x0ad50, 0x055a0, 0x0aba4, 0x0a5b0, 0x052b0,
    0x0b273, 0x06930, 0x07337, 0x06aa0, 0x0ad50, 0x14b55, 0x04b60, 0x0a570, 0x054e4, 0x0d160,
    0x0e968, 0x0d520, 0x0daa0, 0x16aa6, 0x056d0, 0x04ae0, 0x0a9d4, 0x0a2d0, 0x0d150, 0x0f252,
    0x0d520,
]

const code = (year: number) => YEARS[year - BASE]
const leapMonth = (year: number) => code(year) & 0xf
const leapDays = (year: number) => {
    if (!leapMonth(year)) return 0
    return (code(year) & 0x10000) ? 30 : 29
}
const monthDays = (year: number, month: number) =>
    (code(year) & (0x10000 >> month)) ? 30 : 29

const yearDays = (year: number) => {
    let days = 348
    for (let bit = 0x8000; bit > 0x8; bit >>= 1) {
        if (code(year) & bit) days++
    }
    return days + leapDays(year)
}

const inRange = (year: number) => year >= BASE && year <= LAST && year - BASE < YEARS.length

// Gregorian UTC midnight of lunar `month`/`day` in the lunar year that starts
// at that Gregorian year's Spring Festival. Undefined outside the table, or
// when the day does not exist in that month.
export function chineseLunarUtc(year: number, month: number, day: number): number | undefined {
    if (!inRange(year) || month < 1 || month > 12 || day < 1) return undefined
    const length = monthDays(year, month)
    if (day > length) return undefined

    let offset = 0
    for (let y = BASE; y < year; y++) offset += yearDays(y)
    const leap = leapMonth(year)
    for (let m = 1; m < month; m++) {
        offset += monthDays(year, m)
        if (leap === m) offset += leapDays(year)
    }
    offset += day - 1
    return EPOCH + offset * DAY
}

// Qingming in the 21st century: 4 or 5 April.
export function qingmingUtc(year: number): number | undefined {
    if (year < 2000 || year > 2099) return undefined
    const y = year % 100
    const day = Math.floor(y * 0.2422 + 4.81) - Math.floor(y / 4)
    return Date.UTC(year, 3, day)
}
